import React, { useState, useEffect, useCallback } from "react";
import { FaPlus, FaSearch, FaFilter, FaSync } from "react-icons/fa";
import Swal from "sweetalert2";
import axios from "axios";

import CouponStats from "./components/CouponStats";
import CouponCard, { SkeletonCard } from "./components/CouponCard";
import CouponModal from "./components/CouponModal";
import Pagination from "Components/Pagination/Pagination";

// ─── Axios instance ──────────────────────────────────────────────────────────
const api = axios.create({
  baseURL: `${process.env.REACT_APP_API_URL}/coupon-code`,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ─── Toast helper ─────────────────────────────────────────────────────────────
const Toast = Swal.mixin({
  toast: true,
  position: "top-end",
  showConfirmButton: false,
  timer: 3000,
  timerProgressBar: true,
});

const LIMIT = 6;

// ─── Main Component ──────────────────────────────────────────────────────────
const UserCouponCode = () => {
  const [coupons, setCoupons] = useState([]);
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const { data } = await api.get("/admin/coupons/stats");
      setStats(data.data);
    } catch (_) {
    } finally {
      setStatsLoading(false);
    }
  }, []);

  const fetchCoupons = useCallback(
    async (page = 1, searchVal = search, statusVal = statusFilter) => {
      setLoading(true);
      try {
        const params = {
          page,
          limit: LIMIT,
          sortBy: "createdAt",
          order: "desc",
        };
        if (searchVal.trim()) params.search = searchVal.trim();
        if (statusVal) params.status = statusVal;

        const { data } = await api.get("/admin/coupons", { params });
        setCoupons(data.data || []);
        setTotalPages(data.meta?.totalPages || 1);
        setTotalCount(data.meta?.total || 0);
        setCurrentPage(page);
      } catch (err) {
        Toast.fire({
          icon: "error",
          title:
            err.response?.data?.message ||
            err.message ||
            "Failed to fetch coupons.",
        });
      } finally {
        setLoading(false);
      }
    },
    [search, statusFilter]
  );

  useEffect(() => {
    fetchCoupons(1);
    fetchStats();
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      fetchCoupons(1, search, statusFilter);
    }, 400);
    return () => clearTimeout(t);
  }, [search]);

  const handleStatusFilter = (val) => {
    setStatusFilter(val);
    fetchCoupons(1, search, val);
  };

  const handleCreate = async (formData) => {
    setFormLoading(true);
    try {
      await api.post("/admin/coupons", formData);
      setModalOpen(false);
      Toast.fire({ icon: "success", title: "Coupon created successfully!" });
      fetchCoupons(1);
      fetchStats();
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.response?.data?.message || err.message,
      });
    } finally {
      setFormLoading(false);
    }
  };

  const handleUpdate = async (formData) => {
    if (!editData?._id) return;
    setFormLoading(true);
    try {
      const payload = { ...formData };
      // delete payload.couponCode;
      await api.patch(`/admin/coupons/${editData._id}`, payload);
      setModalOpen(false);
      setEditData(null);
      Toast.fire({ icon: "success", title: "Coupon updated successfully!" });
      fetchCoupons(currentPage);
      fetchStats();
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.response?.data?.message || err.message,
      });
    } finally {
      setFormLoading(false);
    }
  };

  const handleToggle = async (coupon) => {
    const next = coupon.status === "active" ? "inactive" : "active";
    const conf = await Swal.fire({
      title: `${next === "active" ? "Activate" : "Deactivate"} coupon?`,
      text: `"${coupon.couponCode}" will be set to ${next}.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, proceed",
      confirmButtonColor: "#db2777",
      cancelButtonText: "Cancel",
    });
    if (!conf.isConfirmed) return;
    try {
      const { data } = await api.patch(
        `/admin/coupons/${coupon._id}/toggle-status`
      );
      Toast.fire({
        icon: "success",
        title: `Coupon is now ${data.data?.status}.`,
      });
      fetchCoupons(currentPage);
      fetchStats();
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.response?.data?.message || err.message,
      });
    }
  };

  const handleDelete = async (coupon) => {
    const conf = await Swal.fire({
      title: "Delete coupon?",
      text: `"${coupon.couponCode}" will be permanently removed.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
      confirmButtonColor: "#dc2626",
      cancelButtonText: "Cancel",
    });
    if (!conf.isConfirmed) return;
    try {
      await api.delete(`/admin/coupons/${coupon._id}`);
      Toast.fire({ icon: "success", title: "Coupon deleted." });
      const newPage =
        coupons.length === 1 && currentPage > 1 ? currentPage - 1 : currentPage;
      fetchCoupons(newPage);
      fetchStats();
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.response?.data?.message || err.message,
      });
    }
  };

  const openCreate = () => {
    setEditData(null);
    setModalOpen(true);
  };
  const openEdit = (c) => {
    setEditData(c);
    setModalOpen(true);
  };
  const closeModal = () => {
    setModalOpen(false);
    setEditData(null);
  };

  return (
    <div className="mt-4 min-h-screen  ">
      <div className="mx-auto ">
        {/* Page header */}
        {/* <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-gray-800 sm:text-2xl">
              Coupon codes
            </h1>
            <p className="mt-0.5 text-sm text-gray-500">
              Manage promo codes and discount rules
            </p>
          </div>
          <button
            onClick={() => {
              fetchCoupons(currentPage);
              fetchStats();
            }}
            className="flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-400 transition-colors hover:bg-gray-50 hover:text-gray-600"
            title="Refresh"
          >
            <FaSync className={`text-sm ${loading ? "animate-spin" : ""}`} />
          </button>
        </div> */}

        {/* Stats */}
        <CouponStats stats={stats} loading={statsLoading} />

        {/* Toolbar */}
        <div className="mb-5 flex flex-col gap-2.5 sm:flex-row sm:items-center">
          {/* Search */}
          <div className="relative flex-1">
            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-gray-400" />
            <input
              type="text"
              placeholder="Search coupon code..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-8 pr-3 text-sm text-gray-700 placeholder-gray-400 outline-none focus:border-pink-400 focus:ring-2 focus:ring-pink-100"
            />
          </div>

          {/* Status filter */}
          <div className="flex items-center gap-2">
            <FaFilter className="text-xs text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => handleStatusFilter(e.target.value)}
              className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-600 outline-none focus:border-pink-400 focus:ring-2 focus:ring-pink-100"
            >
              <option value="">All status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          {/* Add button */}
          <button
            onClick={openCreate}
            className="flex items-center justify-center gap-2 rounded-lg bg-pink-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-pink-700"
          >
            <FaPlus className="text-xs" />
            Add coupon
          </button>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        ) : coupons.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-xl border border-gray-100 bg-white">
              <FaSearch className="text-xl text-gray-300" />
            </div>
            <p className="text-sm font-medium text-gray-600">
              No coupons found
            </p>
            <p className="mt-1 text-xs text-gray-400">
              {search || statusFilter
                ? "Try adjusting your search or filter."
                : "Click 'Add coupon' to create your first one."}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {coupons.map((coupon, i) => (
              <CouponCard
                key={coupon._id}
                coupon={coupon}
                index={i + (currentPage - 1) * LIMIT}
                onEdit={openEdit}
                onDelete={handleDelete}
                onToggle={handleToggle}
              />
            ))}
          </div>
        )}

        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <div className="mt-5">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={(p) => fetchCoupons(p)}
            />
          </div>
        )}
      </div>

      {/* Modal */}
      <CouponModal
        isOpen={modalOpen}
        onClose={closeModal}
        onSubmit={editData ? handleUpdate : handleCreate}
        editData={editData}
        loading={formLoading}
      />
    </div>
  );
};

export default UserCouponCode;
